import React from 'react';
import { Link2, RefreshCw, Unlink } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { EmptyState } from '../../../components/shared/EmptyState';
import { useGithub, type GithubInstallation, type GithubRepo } from '../../../core/hooks/useGithub';

interface ClientGithubTabProps {
  clientId: string;
  currentRepoId?: number;
  currentRepoOwner?: string;
  currentRepoName?: string;
  currentInstallationId?: number | string;
  currentSync?: {
    issuesIn?: boolean;
    branchesIn?: boolean;
    taskWriteBack?: boolean;
  };
}

export const ClientGithubTab: React.FC<ClientGithubTabProps> = ({
  clientId,
  currentRepoId,
  currentRepoOwner,
  currentRepoName,
  currentInstallationId,
  currentSync,
}) => {
  const {
    config,
    getInstallations,
    getRepos,
    linkClientRepo,
    unlinkClientRepo,
    setClientSyncOptions,
    syncClientRepo,
  } = useGithub();

  const [installations, setInstallations] = React.useState<GithubInstallation[]>([]);
  const [repos, setRepos] = React.useState<GithubRepo[]>([]);
  const [selectedInstallationId, setSelectedInstallationId] = React.useState<string>(
    currentInstallationId ? String(currentInstallationId) : ''
  );
  const [selectedRepoId, setSelectedRepoId] = React.useState<string>(currentRepoId ? String(currentRepoId) : '');
  const [loadingInstallations, setLoadingInstallations] = React.useState(false);
  const [loadingRepos, setLoadingRepos] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const lastLoadedInstallationIdRef = React.useRef<string>('');
  const [syncOptions, setSyncOptions] = React.useState({
    issuesIn: currentSync?.issuesIn ?? true,
    branchesIn: currentSync?.branchesIn ?? true,
    taskWriteBack: currentSync?.taskWriteBack ?? false,
  });

  const connectedRepoLabel = currentRepoOwner && currentRepoName ? `${currentRepoOwner}/${currentRepoName}` : null;

  React.useEffect(() => {
    if (!config?.connected) {
      return;
    }

    const loadInstallations = async () => {
      try {
        setLoadingInstallations(true);
        const items = await getInstallations();
        setInstallations(items);
      } catch (error) {
        console.error('Failed to load GitHub installations:', error);
      } finally {
        setLoadingInstallations(false);
      }
    };

    loadInstallations();
  }, [config?.connected, getInstallations]);

  const loadRepos = React.useCallback(async (installationId: string) => {
    if (!installationId || lastLoadedInstallationIdRef.current === installationId) {
      return;
    }

    try {
      setLoadingRepos(true);
      lastLoadedInstallationIdRef.current = installationId;
      const items = await getRepos(installationId);
      setRepos(items);
    } catch (error) {
      lastLoadedInstallationIdRef.current = '';
      console.error('Failed to load repositories:', error);
      alert('Failed to load repositories for selected installation.');
    } finally {
      setLoadingRepos(false);
    }
  }, [getRepos]);

  React.useEffect(() => {
    if (!installations.length || selectedInstallationId) {
      return;
    }

    const firstInstallationId = String(installations[0].id);
    setSelectedInstallationId(firstInstallationId);
  }, [installations, selectedInstallationId]);

  React.useEffect(() => {
    if (!selectedInstallationId) {
      return;
    }

    void loadRepos(selectedInstallationId);
  }, [loadRepos, selectedInstallationId]);

  const handleLinkRepo = async () => {
    const repo = repos.find((r) => String(r.id) === selectedRepoId);
    if (!repo) {
      alert('Select a repository first.');
      return;
    }

    try {
      setSaving(true);
      await linkClientRepo(clientId, {
        ...repo,
        installationId: selectedInstallationId || repo.installationId,
      });
      await setClientSyncOptions(clientId, syncOptions);
      alert('Repository linked successfully.');
    } catch (error) {
      console.error('Failed to link repository:', error);
      alert('Failed to link repository.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkRepo = async () => {
    if (!window.confirm('Unlink this project from GitHub repository?')) {
      return;
    }

    try {
      setSaving(true);
      await unlinkClientRepo(clientId);
      alert('Repository unlinked.');
    } catch (error) {
      console.error('Failed to unlink repository:', error);
      alert('Failed to unlink repository.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSyncOptions = async () => {
    try {
      setSaving(true);
      await setClientSyncOptions(clientId, syncOptions);
      alert('Sync options updated.');
    } catch (error) {
      console.error('Failed to update sync options:', error);
      alert('Failed to update sync options.');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      const result = await syncClientRepo(clientId);
      alert(`Sync complete. ${result.branches} branches and ${result.issues} issues synced.`);
    } catch (error) {
      console.error('Failed to sync repository:', error);
      alert('Failed to sync repository.');
    } finally {
      setSyncing(false);
    }
  };

  if (!config?.connected) {
    return (
      <EmptyState
        title="GitHub not connected"
        description="Connect GitHub in Settings before linking repositories to projects."
        action={{
          label: 'Go to Settings',
          onClick: () => (window.location.href = '/settings'),
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Repository Mapping</h3>
            <p className="text-sm text-muted-foreground">
              Map one GitHub repository to this project for branch and issue sync.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Installation</label>
            <Select
              value={selectedInstallationId}
              onValueChange={(value) => {
                setSelectedInstallationId(value);
                setSelectedRepoId('');
                setRepos([]);
                lastLoadedInstallationIdRef.current = '';
              }}
              disabled={loadingInstallations}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingInstallations ? 'Loading installations...' : 'Select installation'} />
              </SelectTrigger>
              <SelectContent>
                {installations.map((installation) => (
                  <SelectItem key={String(installation.id)} value={String(installation.id)}>
                    {installation.accountLogin} ({installation.accountType})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Repository</label>
            <Select
              value={selectedRepoId}
              onValueChange={setSelectedRepoId}
              disabled={!selectedInstallationId || loadingRepos}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingRepos ? 'Loading repositories...' : 'Select repository'} />
              </SelectTrigger>
              <SelectContent>
                {repos.map((repo) => (
                  <SelectItem key={repo.id} value={String(repo.id)}>
                    {repo.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleLinkRepo} disabled={!selectedRepoId || saving}>
              <Link2 className="mr-2 h-4 w-4" />
              Link Repository
            </Button>
            <Button variant="outline" onClick={handleUnlinkRepo} disabled={!connectedRepoLabel || saving}>
              <Unlink className="mr-2 h-4 w-4" />
              Unlink
            </Button>
          </div>

          {connectedRepoLabel && (
            <div className="rounded-md border p-3 text-sm">
              <span className="font-medium">Currently linked:</span> {connectedRepoLabel}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Sync Settings</h3>
            <p className="text-sm text-muted-foreground">
              Choose what to sync from GitHub into admin task workflows.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={syncOptions.branchesIn}
              onCheckedChange={(checked) => setSyncOptions((prev) => ({ ...prev, branchesIn: checked === true }))}
            />
            Sync branches into project task context
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={syncOptions.issuesIn}
              onCheckedChange={(checked) => setSyncOptions((prev) => ({ ...prev, issuesIn: checked === true }))}
            />
            Sync issues into project task context
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={syncOptions.taskWriteBack}
              onCheckedChange={(checked) => setSyncOptions((prev) => ({ ...prev, taskWriteBack: checked === true }))}
            />
            Enable task write-back (phase 2)
          </label>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSaveSyncOptions} disabled={saving}>
              Save Sync Options
            </Button>
            <Button onClick={handleSyncNow} disabled={!connectedRepoLabel || syncing}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
